import React, { useState } from 'react';
import {
  MapPin,
  ExternalLink,
  Sparkles,
  ArrowRight,
  Search,
  Grid3X3,
  ChevronRight,
  Star,
  Zap,
  Shield,
  Users,
} from 'lucide-react';
import { GrantConsultant } from './components/onboarding/GrantConsultant';
import { ReferralTrigger } from './components/layout/ReferralTrigger';
import { categories, connectedApps } from './config/categories';
import { ConnectedApp } from './types/grant';

type ViewMode = 'home' | 'categories' | 'apps';

const App: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('home');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredApps = connectedApps.filter((app) => {
    const matchesSearch =
      searchQuery === '' ||
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAppClick = (app: ConnectedApp) => {
    window.open(app.url, '_blank');
  };

  const renderAppCard = (app: ConnectedApp) => {
    const category = categories.find((c) => c.id === app.category);

    return (
      <div
        key={app.id}
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-maple-200 transition-all group cursor-pointer"
        onClick={() => handleAppClick(app)}
      >
        {/* Card Header */}
        <div className={`p-5 bg-gradient-to-r ${category?.bgGradient || 'from-slate-500 to-slate-600'}`}>
          <div className="flex items-start justify-between">
            <span className="text-4xl">{app.icon}</span>
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              {category?.name || 'General'}
            </span>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-5">
          <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-maple-600 transition-colors">
            {app.name}
          </h3>
          <p className="text-slate-600 text-sm mb-4 line-clamp-2">{app.description}</p>

          {/* Features */}
          <ul className="space-y-2 mb-4">
            {app.features.slice(0, 3).map((feature, idx) => (
              <li key={idx} className="text-sm text-slate-500 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-maple-400 rounded-full flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold group-hover:bg-maple-600 group-hover:text-white transition-all flex items-center justify-center gap-2">
            Open Tool
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => setViewMode('home')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-maple-500 to-maple-700 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Grant Hub</h1>
                <p className="text-xs text-slate-500">Canadian Funding Finder</p>
              </div>
            </button>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              <button
                onClick={() => setViewMode('home')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'home'
                    ? 'bg-maple-100 text-maple-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => setViewMode('categories')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'categories'
                    ? 'bg-maple-100 text-maple-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Categories
              </button>
              <button
                onClick={() => setViewMode('apps')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'apps'
                    ? 'bg-maple-100 text-maple-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                All Tools
              </button>
            </nav>

            {/* Mobile Menu */}
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={() => setViewMode('categories')}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <Grid3X3 size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {viewMode === 'home' && (
          <>
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-maple-600 via-maple-700 to-maple-800 text-white py-16 px-4">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Sparkles size={16} />
                  AI-Powered Grant Finding
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Find Canadian Grants
                  <br />
                  <span className="text-maple-200">Made Simple</span>
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
                  Connect to specialized grant search tools for disability support, small business
                  funding, and more. Our AI helps you find the right resources.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => setViewMode('apps')}
                    className="px-8 py-4 bg-white text-maple-700 rounded-xl font-bold text-lg hover:bg-slate-100 transition-colors shadow-lg flex items-center justify-center gap-2"
                  >
                    Browse Tools
                    <ArrowRight size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('categories')}
                    className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
                  >
                    View Categories
                    <Grid3X3 size={20} />
                  </button>
                </div>
              </div>
            </section>

            {/* AI Consultant Section */}
            <section className="py-12 px-4 -mt-8">
              <div className="max-w-4xl mx-auto">
                <GrantConsultant onCategorySelect={(cat) => setSelectedCategory(cat)} />
              </div>
            </section>

            {/* Features Section */}
            <section className="py-12 px-4 bg-white">
              <div className="max-w-6xl mx-auto">
                <h3 className="text-2xl font-bold text-center text-slate-800 mb-8">
                  Why Use Grant Hub?
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6">
                    <div className="w-14 h-14 bg-maple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Zap className="w-7 h-7 text-maple-600" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-2">Specialized Tools</h4>
                    <p className="text-slate-600 text-sm">
                      Purpose-built apps for different grant categories, each optimized for specific
                      needs.
                    </p>
                  </div>
                  <div className="text-center p-6">
                    <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-7 h-7 text-emerald-600" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-2">Accessible Design</h4>
                    <p className="text-slate-600 text-sm">
                      Tools designed with accessibility in mind, including support for various
                      reading levels.
                    </p>
                  </div>
                  <div className="text-center p-6">
                    <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-7 h-7 text-blue-600" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-2">AI Guidance</h4>
                    <p className="text-slate-600 text-sm">
                      Our AI consultant helps you find the right tool and grants for your specific
                      situation.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Access Cards */}
            <section className="py-12 px-4">
              <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-slate-800">Available Tools</h3>
                  <button
                    onClick={() => setViewMode('apps')}
                    className="text-maple-600 font-semibold flex items-center gap-1 hover:text-maple-700"
                  >
                    View All <ChevronRight size={18} />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {connectedApps.map(renderAppCard)}
                </div>
              </div>
            </section>
          </>
        )}

        {viewMode === 'categories' && (
          <section className="py-12 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-slate-800 mb-3">Grant Categories</h2>
                <p className="text-slate-600">Select a category to find relevant funding tools</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category) => {
                  const appsInCategory = connectedApps.filter(
                    (app) => app.category === category.id
                  );
                  return (
                    <div
                      key={category.id}
                      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-maple-200 transition-all cursor-pointer group"
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setViewMode('apps');
                      }}
                    >
                      <div
                        className={`p-6 bg-gradient-to-r ${category.bgGradient} text-white`}
                      >
                        <span className="text-5xl mb-4 block">{category.icon}</span>
                        <h3 className="text-xl font-bold">{category.name}</h3>
                      </div>
                      <div className="p-5">
                        <p className="text-slate-600 text-sm mb-4">{category.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">
                            {appsInCategory.length} tool{appsInCategory.length !== 1 ? 's' : ''}{' '}
                            available
                          </span>
                          <ArrowRight
                            size={18}
                            className="text-slate-400 group-hover:text-maple-600 group-hover:translate-x-1 transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {viewMode === 'apps' && (
          <section className="py-8 px-4">
            <div className="max-w-6xl mx-auto">
              {/* Split Screen Layout */}
              <div className="flex flex-col lg:flex-row gap-6 split-view-container">
                {/* Left Panel - Filters */}
                <div className="lg:w-80 flex-shrink-0">
                  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden sticky top-24">
                    <div className="bg-slate-100 px-5 py-4 border-b border-slate-200">
                      <h3 className="font-bold text-slate-700 flex items-center gap-2">
                        <Search size={18} />
                        Search & Filter
                      </h3>
                    </div>
                    <div className="p-5 space-y-5">
                      {/* Search */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Search Tools
                        </label>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search by name..."
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-maple-500 focus:border-maple-500 transition-all"
                        />
                      </div>

                      {/* Category Filter */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Category
                        </label>
                        <select
                          value={selectedCategory || ''}
                          onChange={(e) => setSelectedCategory(e.target.value || null)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-maple-500 focus:border-maple-500 transition-all bg-white"
                        >
                          <option value="">All Categories</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.icon} {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Clear Filters */}
                      {(searchQuery || selectedCategory) && (
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedCategory(null);
                          }}
                          className="w-full py-2 text-maple-600 font-medium hover:bg-maple-50 rounded-lg transition-colors"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Panel - Results */}
                <div className="flex-1">
                  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="bg-maple-50 px-5 py-4 border-b border-maple-100">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-maple-700 flex items-center gap-2">
                          <Star size={18} />
                          Available Tools
                        </h3>
                        <span className="text-sm text-maple-600">
                          {filteredApps.length} result{filteredApps.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      {filteredApps.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-5">
                          {filteredApps.map(renderAppCard)}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Search size={48} className="mx-auto text-slate-300 mb-4" />
                          <h4 className="text-lg font-semibold text-slate-600 mb-2">
                            No tools found
                          </h4>
                          <p className="text-slate-500 text-sm">
                            Try adjusting your search or filters
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 px-4 mt-auto">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-maple-500 to-maple-700 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-slate-700">Grant Hub</span>
            </div>
            <p className="text-sm text-slate-500 text-center">
              Connecting Canadians to funding opportunities. AI can make mistakes - always verify
              information.
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>Made in Canada</span>
              <span className="text-2xl">🍁</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Referral Button */}
      <ReferralTrigger />
    </div>
  );
};

export default App;
